/// <reference path="../../ng-services/chat.service.ts"/>
/// <reference path="../../ng-components/load-more-button/scroll-keeper.factory.ts"/>

import { IReactionDisposer, when } from 'mobx';
import { ChatMessageID, Meeting } from '../../models/chat';
import { ChatConversationService, ChatViewMode } from '../../ng-services/chat-conversation.service';
import { ChatMessageSenderService } from '../../ng-services/chat-message-sender.service';
import { ChatConversationStore, chatStore } from '../../ng-services/chat.store';
import { ConversationsStore } from '../../ng-services/conversations.store';
import { MeetingsStore } from '../../ng-services/meetings.store';
import { ScrollKeeper, ScrollKeeperFactory } from '../load-more-button/scroll-keeper.factory';

(function () {
    const PUBLIC_CHAT_ID = 0;

    class ChatConversationComponent implements ng.IOnInit, ng.IOnChanges {
        // Store with conversation data
        public chatConversationStore!: ChatConversationStore;

        public conversation!: Meeting;
        public showHeader: boolean = true;

        // Show loading spinner when true. Set to false when messages are loaded.
        get isLoading(): boolean {
            return this.chatConversationStore.isLoading;
        }

        public unreadMessages: number = 0;

        public loadingPreviousMessages = false;
        public hasMorePreviousMessages = true;
        public hasNextMessages = false;
        public scrollKeeper!: ScrollKeeper;

        public isProcessingUploads!: boolean;

        // We can switch between different views in the messages area.
        public currentView?: ChatViewMode;

        /** Search term that we want to filter for. We use it to highlight the term in message body. */
        public searchTerm?: string;

        public isFollowingMessages!: boolean;

        // We are watching if chat area gets resized.
        // When it happens we can move scroll to the bottom if we should stick to it.
        private resizeObserver?: ResizeObserver;

        /**
         * ID of the message that should be highlighted.
         *
         * Set it when you change the "goto" message in order to give a visual hint of where the message is in the conversation list.
         */
        public highlightMessageId?: ChatMessageID;

        // todo: refactor
        private subscribes!: (Rx.IDisposable | IReactionDisposer)[];

        static $inject = [
            '$timeout',
            '$element',
            'ConversationsStore',
            'ChatConversationService',
            'ChatMessageSenderService',
            'MeetingsStore',
            'scrollKeeper',
        ];

        constructor(
            private $timeout: angular.ITimeoutService,
            private $element: angular.IRootElementService,
            private conversationsStore: ConversationsStore,
            private chatConversationService: ChatConversationService,
            private chatMessageSenderService: ChatMessageSenderService,
            private meetingsStore: MeetingsStore,
            private scrollKeeperFactory: ScrollKeeperFactory
        ) {}

        get isSearchResultView() {
            return this.currentView == ChatViewMode.SEARCH_RESULT;
        }

        get hasMessages() {
            return (this.chatConversationStore?.messages.length ?? 0) > 0;
        }

        get isPublicChat() {
            return this.conversation.id === PUBLIC_CHAT_ID;
        }

        // We don't need to do anything here for now, because we re-initialize the conversation component
        // with a ng-repeat hack.
        $onChanges(onChangesObj: angular.IOnChangesObject) {}

        $onInit() {
            this.chatConversationStore = chatStore.getOrCreateConversation(this.conversation.id);

            // Initiate the component by fetching chat messages
            // REFACTOR: some kind of intermediate state of messages could be nice to indicate that we did not load
            //           any messages yet.
            this.chatConversationService.dispatchFetchMessages(this.conversation.id, 'replace');

            this.scrollKeeper = this.scrollKeeperFactory('.chat_conversation__messages');
            const element = this.$element.find('.chat_conversation__messages')[0];

            const elementScrolled$ = Rx.Observable.fromEvent(element, 'scroll');

            if (this.supportsResizeObserver) {
                this.initResizeObserver();
            }

            this.subscribes = [
                this.meetingsStore.isStuckToBottom$.subscribe((v) =>
                    this.$timeout(() => {
                        this.isFollowingMessages = v;
                    })
                ),
                this.chatConversationService.viewMode$.subscribe((viewMode) => {
                    this.$timeout(() => {
                        this.currentView = viewMode;
                    });
                }),

                this.chatConversationService.searchTerm$.subscribe((searchTerm) => {
                    this.$timeout(() => (this.searchTerm = searchTerm));
                }),

                this.chatMessageSenderService.isProcessingUploads.subscribe((v) => {
                    this.isProcessingUploads = v;
                }),

                this.chatConversationService.getConversation$(this.conversation.id).subscribe((c) => {
                    this.$timeout(() => {
                        this.hasNextMessages = c.hasNextMessages;
                    });
                }),

                // Highlight and scroll into view a message that we want to "go to".
                this.chatConversationService.gotoMessage$.subscribe((message) => {
                    this.highlightMessageId = message?.id ?? undefined;

                    if (!message) return;

                    this.$timeout(() => {
                        const messageEl = this.$element.find(`#chat-message-${message.id}`)[0];
                        // make sure we can see the message in viewport
                        requestAnimationFrame(() => messageEl.scrollIntoView({ behavior: 'auto', block: 'center' }));
                    });
                }),

                elementScrolled$
                    .map((_) => this.isAtBottom(element))
                    .distinctUntilChanged()
                    .subscribe((isAtBottom) => {
                        // We are not "following" latest messages if we are at the bottom, but have messages to load
                        if (isAtBottom && this.hasNextMessages) return;

                        this.meetingsStore.dispatchIsStuckToBottom(isAtBottom);
                    }),

                this.meetingsStore.meetings$
                    .map((meetings) => meetings.find((m) => m.id == this.conversation.id)?.newMessageCount || 0)
                    .subscribe((unreadCount) => this.$timeout(() => (this.unreadMessages = unreadCount))),

                // REFACTOR: not exactly correct, since no messages also means the messages have been loaded
                when(
                    () => this.chatConversationStore.messages.length > 0,
                    () => {
                        this.isAtBottom(element) && this.scrollToBottom();
                    }
                ),
            ];
        }

        scrollToBottom() {
            const element = this.$element.find('.chat_conversation__messages')[0];
            requestAnimationFrame(() => (element.scrollTop = element.scrollHeight));
        }

        get supportsResizeObserver() {
            return 'ResizeObserver' in window;
        }

        initResizeObserver() {
            const wrapper = this.$element.find('.chat_conversation__messages')[0];
            const innerWrapper = this.$element.find('.chat_conversation__inner_wrapper')[0];

            this.resizeObserver = new ResizeObserver(() => {
                if (!this.isFollowingMessages) {
                    return;
                }
                console.log('is following messages - scroll to bottom on resize');
                this.scrollToBottom();
            });
            this.resizeObserver.observe(innerWrapper);
            this.resizeObserver.observe(wrapper);
        }

        distanceToBottom(el: Element) {
            return Math.max(el.scrollHeight - Math.ceil(el.scrollTop) - el.clientHeight, 0);
        }

        isAtBottom(el: Element) {
            return this.distanceToBottom(el) == 0;
        }

        loadPreviousMessages() {
            this.scrollKeeper.rememberPosition();
            this.loadingPreviousMessages = true;

            this.chatConversationService.fetchPreviousMessages(this.conversation.id).then(() =>
                this.$timeout(() => {
                    this.loadingPreviousMessages = false;
                    this.scrollKeeper.restorePosition();
                })
            );
        }

        gotoLatest() {
            this.chatConversationService.gotoLatestMessage(this.conversation.id);
        }

        $onDestroy() {
            this.subscribes.forEach((disposable) => {
                'dispose' in disposable ? disposable.dispose() : disposable();
            });

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }

        trackBy(message: any) {
            // IMPORTANT! The `message.id` is NOT unique! It's a timestamp.
            //            We already had cases where ppl sent a message in exactly the same MICROSECOND.
            // PRIMARY KEY for the "message" table is: id, entry_id, entry, user_id, type.
            // We are already scoped by entry and entry_id. Our ID should be id, user_id, type.
            const uniqueMessageId = [message.id, message.owner?.id, message.type].join('-');
            return message.guid || uniqueMessageId;
        }

        get shouldShowJumpToLatestButton() {
            // do not show jump to latest when we are in loading state
            if (this.chatConversationStore.isLoading) {
                return false;
            }

            // show it when we have more messages available to load
            if (this.hasNextMessages) {
                return true;
            }

            // show when we have unread messages and we arent at the bottom of the chat
            if (this.unreadMessages > 0 && !this.isFollowingMessages) {
                return true;
            }

            return false;
        }
    }

    angular.module('tradersyard').component('tyChatConversation', {
        // language=HTML
        template: `
            <div class="chat_conversation" ng-class="{'chat_conversation--search-result': $ctrl.isSearchResultView}" mobx-autorun>
                <div class="chat_conversation__header" ng-if="$ctrl.showHeader">
                    <ty-chat-conversation-header meeting="$ctrl.conversation" is-public-chat="$ctrl.isPublicChat"/>
                </div>
                <div class="chat_conversation__info" ng-if="$ctrl.isLoading">
                    <ty-loading-spinner/>
                </div>
                <div class="chat_conversation__info" ng-if="!$ctrl.isLoading && !$ctrl.hasMessages">
                    No messages <br>
                    Write your first message below!
                </div>
                <div is-uib ng-if="$ctrl.isProcessingUploads" class="chat_conversation__upload_progress">
                    <uib-progressbar class="progress-striped active" value="100"/>
                </div>

                <div class="chat_conversation__messages_wrapper">
                    <div class="chat_conversation__messages" ng-show="$ctrl.hasMessages && !$ctrl.isLoading">
                        <ty-conversation-previous-message-loader is-loading="$ctrl.loadingPreviousMessages"
                                                                 has-more="$ctrl.chatConversationStore.hasPreviousMessages"
                                                                 on-click="$ctrl.loadPreviousMessages()"/>
                        <div class="chat_conversation__inner_wrapper">
                            <div class="chat_conversation__group"
                                 ng-repeat="group in $ctrl.chatConversationStore.groupedMessages track by $ctrl.trackBy(group.root)">
                                <ty-chat-message ng-repeat="message in group.children track by $ctrl.trackBy(message)"
                                                 message="message"
                                                 first="$first"
                                                 search-term="$ctrl.searchTerm"
                                                 is-public-chat="$ctrl.isPublicChat"
                                                 ng-class="{'chat_conversation__message--highlight': message.id == $ctrl.highlightMessageId }"/>
                            </div>
                        </div>

                    </div>
                    <a class="chat_conversation__sticky_button"
                       href="#"
                       ng-if="$ctrl.shouldShowJumpToLatestButton"
                       ng-click="$ctrl.gotoLatest()">
                        <span ng-if="$ctrl.unreadMessages == 0">Jump to latest <i class="fa fa-chevron-down"></i></span>
                        <span ng-if="$ctrl.unreadMessages == 1">New message <i class="fa fa-chevron-down"></i></span>
                        <span ng-if="$ctrl.unreadMessages > 1">{{$ctrl.unreadMessages}} new messages <i
                            class="fa fa-chevron-down"></i></span>
                    </a>
                </div>

                <ty-chat-currently-typing-indicator conversation="$ctrl.conversation" ng-if="!$ctrl.isPublicChat"/>
                <ty-chat-message-input conversation="$ctrl.conversation" ng-hide="$ctrl.isSearchResultView" ng-if="!$ctrl.isPublicChat"/>
            </div>
        `,
        controller: ChatConversationComponent,
        bindings: {
            conversation: '<',
            showHeader: '<?',
        },
    });
})();
